import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { useUnmount } from "app/custom-hooks/useUnmount";
import demoModeTrackingService from "app/pages/sales-intelligence/services/tracking/demo-mode/trackingService";
import {
    useDemoModeStore,
    useEnteringDemo,
    useVisibleDemo,
} from "app/pages/sales-intelligence/store/demo-mode/useDemoModeStore";
import { demoModeKeys } from "app/pages/sales-intelligence/utils/i18n/demo-mode";

import { canRequestDemoAccess } from "../utils/canRequestDemoAccess";

import { DemoModeAccessRequestCta } from "./DemoModeAccessRequestCta";
import {
    AnimatedBannerContainer,
    AnimatedBannerRow,
    BANNER_ENTER_DURATION_MS,
    DemoModeBannerContent,
    DemoModeBannerWrapper,
    DemoModeCloseButton,
} from "./styles";

const prefersReducedMotion = (): boolean => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

type DemoModeBannerProps = {
    /**
     * Rendered beside the access-request CTA. A slot rather than a lookup on the session: this lives in
     * `common-components`, which may not import the feature modules an action would come from.
     */
    readonly primaryAction?: ReactNode;
};

export const DemoModeBanner = ({ primaryAction }: DemoModeBannerProps) => {
    const { translate } = useTranslate();

    const visibleDemo = useVisibleDemo();
    const enteringDemo = useEnteringDemo();
    const exitDemo = useDemoModeStore((state) => state.exitDemo);

    const canRequestAccess = canRequestDemoAccess();

    useUnmount(() => useDemoModeStore.getState().exitDemo());

    useEffect(() => {
        if (!enteringDemo) {
            return undefined;
        }

        const commit = () => useDemoModeStore.getState().commitEnteringDemo();

        // With motion reduced there is no entrance to wait out, and no `transitionend` would fire either.
        if (prefersReducedMotion()) {
            commit();

            return undefined;
        }

        const timeoutId = setTimeout(commit, BANNER_ENTER_DURATION_MS);

        return () => clearTimeout(timeoutId);
    }, [enteringDemo]);

    const lastDemoRef = useRef(visibleDemo);
    if (visibleDemo) {
        lastDemoRef.current = visibleDemo;
    }
    const demo = visibleDemo ?? lastDemoRef.current;

    return (
        <AnimatedBannerContainer
            $isOpen={Boolean(visibleDemo)}
            data-automation="demo-mode-banner"
        >
            <AnimatedBannerRow>
                {demo ? (
                    <DemoModeBannerWrapper>
                        <DemoModeBannerContent>
                            <Flex
                                inline
                                alignItems="center"
                                gap="6px"
                            >
                                <Text
                                    fontVariant="subtitle2"
                                    color={colorsPalettes.carbon[0]}
                                >
                                    <strong>{translate(demoModeKeys.bannerTitle)}</strong>
                                </Text>

                                <Text
                                    fontVariant="body2"
                                    color={colorsPalettes.carbon[0]}
                                >
                                    {translate(demoModeKeys.bannerSubtitle(demo.copyNamespace))}
                                </Text>
                            </Flex>

                            {/* Keyed off `visibleDemo`, not `demo`: the latter is retained through the exit
                                animation, which would leave the action invisible but still focusable. */}
                            {visibleDemo ? primaryAction : null}

                            {canRequestAccess ? <DemoModeAccessRequestCta demo={demo} /> : null}
                        </DemoModeBannerContent>
                        <Tooltip
                            content={translate(demoModeKeys.bannerClose)}
                            placement="left"
                            enabled
                        >
                            <DemoModeCloseButton
                                type="flat"
                                iconName="clear"
                                iconSize="16px"
                                onClick={() => {
                                    demoModeTrackingService.trackBannerCloseClick(demo.id);
                                    exitDemo();
                                }}
                                data-automation="demo-mode-banner-close"
                            />
                        </Tooltip>
                    </DemoModeBannerWrapper>
                ) : null}
            </AnimatedBannerRow>
        </AnimatedBannerContainer>
    );
};
